import axios from "axios";
import { Match, NextMatchInfo } from "../types/Match";
import { MatchDaySettings } from "../types/Settings";
import { formatTime } from "../utils/time-utils";
import {
    API_BASE_URL,
    MATCH_END_TIME,
    MATCH_WEEKS_AHEAD,
    MATCH_WEEKS_BACK,
    TEAM_IDS,
} from "../constants/match-constants";

export function getTeamMatchesEndpoint(teamId: number): string {
    return `${API_BASE_URL}/getmatchesbyteamid/${teamId}/${MATCH_WEEKS_BACK}/${MATCH_WEEKS_AHEAD}`;
}

export async function fetchTeamMatches(
    endpoint: string,
    teamId: number
): Promise<Match[]> {
    try {
        const response = await axios.get<Match[]>(endpoint);

        const teamMatches = response.data.filter(
            (match) =>
                match.team1.teamId === teamId || match.team2.teamId === teamId
        );

        // OpenLigaDB is community maintained, so the same match can appear in two leagues
        const seen = new Set<string>();
        return teamMatches.filter((match) => {
            const key = `${match.matchDateTimeUTC}|${match.team1.teamId}|${match.team2.teamId}`;
            if (seen.has(key)) {
                return false;
            }
            seen.add(key);
            return true;
        });
    } catch (error) {
        throw new Error(`Failed to fetch match data: ${error}`);
    }
}

function toNextMatchInfo(
    match: Match,
    teamId: number,
    isRunning: boolean
): NextMatchInfo {
    const isHome = match.team1.teamId === teamId;
    const opponent = isHome ? match.team2 : match.team1;
    const matchDate = new Date(match.matchDateTime);

    return {
        opponent,
        isHome,
        isRunning,
        matchDate,
        formattedDate: isRunning ? "Spiel läuft gerade" : formatTime(matchDate),
    };
}

export function findNextMatch(
    matches: Match[],
    teamId: number
): NextMatchInfo | null {
    const now = new Date();

    const runningMatch = matches.find((match) => {
        const matchStart = new Date(match.matchDateTime);
        const matchEnd = new Date(matchStart.getTime() + MATCH_END_TIME);
        return !match.matchIsFinished && matchStart <= now && now <= matchEnd;
    });

    if (runningMatch) {
        return toNextMatchInfo(runningMatch, teamId, true);
    }

    const upcomingMatches = matches
        .filter((match) => !match.matchIsFinished)
        .filter((match) => new Date(match.matchDateTime) > now)
        .sort(
            (a, b) =>
                new Date(a.matchDateTime).getTime() -
                new Date(b.matchDateTime).getTime()
        );

    if (upcomingMatches.length === 0) {
        return null;
    }

    return toNextMatchInfo(upcomingMatches[0], teamId, false);
}

export async function fetchNextApiMatch(
    settings: MatchDaySettings
): Promise<NextMatchInfo | null> {
    const teamId = settings.source === "women" ? TEAM_IDS.women : TEAM_IDS.men;
    const matches = await fetchTeamMatches(
        getTeamMatchesEndpoint(teamId),
        teamId
    );
    return findNextMatch(matches, teamId);
}

export function getManualMatch(settings: MatchDaySettings): NextMatchInfo | null {
    if (!settings.opponentName || !settings.kickoff) {
        return null;
    }

    const now = new Date();
    const matchDate = new Date(settings.kickoff);
    const matchEnd = new Date(matchDate.getTime() + MATCH_END_TIME);

    if (now > matchEnd) {
        return null;
    }

    const isRunning = matchDate <= now;

    return {
        opponent: {
            teamName: settings.opponentName,
            teamIconUrl: settings.opponentLogoUrl,
        },
        isHome: settings.isHome,
        isRunning,
        matchDate,
        formattedDate: isRunning ? "Spiel läuft gerade" : formatTime(matchDate),
    };
}
